import React from 'react';
import '../modern.css';
import PageLayout from './PageLayout';
import SectionHeading from './SectionHeading';
import ContentCard from './ContentCard';
import useReveal from '../useReveal';
import { pageContent } from './pageContent';

function EPC() {
  useReveal();
  const cards = pageContent.epc.cards;
  return (
    <PageLayout
      pageKey="epc"
      heroVisual={
        <div className="hero-epc" aria-hidden="true">
          <div className="hero-epc__bar">
            <span className="ec-tag">epc_complete.synthetic</span>
            <span className="ec-live"><i className="ec-live-dot" />9.4M records</span>
          </div>

          <div className="hero-epc__tabs">
            <span className="ec-tab ec-tab--1">Coverage</span>
            <span className="ec-tab ec-tab--2">Fire risk</span>
            <span className="ec-tab ec-tab--3">Retrofit</span>
          </div>

          <div className="hero-epc__grid">
            <i className="ec-scanner" />

            <div className="ec-tile">
              <svg viewBox="0 0 24 20"><path d="M3 9.5 12 2l9 7.5V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /></svg>
              <span className="ec-chip ec-chip--b">B</span>
            </div>

            <div className="ec-tile ec-tile--gap" style={{ '--gd': '1.95s' }}>
              <svg viewBox="0 0 24 20"><path d="M3 9.5 12 2l9 7.5V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /></svg>
              <span className="ec-chip ec-chip--none">&ndash;</span>
              <span className="ec-chip ec-chip--synth ec-chip--c">C</span>
            </div>

            <div className="ec-tile ec-tile--fire ec-tile--retro">
              <svg viewBox="0 0 24 20"><path d="M3 9.5 12 2l9 7.5V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /></svg>
              <span className="ec-chip ec-chip--e">E</span>
              <span className="ec-chip ec-chip--upgrade ec-chip--c">C</span>
              <span className="ec-flag">!</span>
            </div>

            <div className="ec-tile">
              <svg viewBox="0 0 24 20"><path d="M3 9.5 12 2l9 7.5V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /></svg>
              <span className="ec-chip ec-chip--a">A</span>
            </div>

            {/* synthesised record surfaces a risk nobody could see before */}
            <div className="ec-tile ec-tile--gap ec-tile--fire" style={{ '--gd': '1.1s' }}>
              <svg viewBox="0 0 24 20"><path d="M3 9.5 12 2l9 7.5V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /></svg>
              <span className="ec-chip ec-chip--none">&ndash;</span>
              <span className="ec-chip ec-chip--synth ec-chip--e">E</span>
              <span className="ec-flag ec-flag--2">!</span>
            </div>

            <div className="ec-tile ec-tile--retro">
              <svg viewBox="0 0 24 20"><path d="M3 9.5 12 2l9 7.5V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /></svg>
              <span className="ec-chip ec-chip--d">D</span>
              <span className="ec-chip ec-chip--upgrade ec-chip--b">B</span>
            </div>

            <div className="ec-tile ec-tile--gap" style={{ '--gd': '2.8s' }}>
              <svg viewBox="0 0 24 20"><path d="M3 9.5 12 2l9 7.5V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /></svg>
              <span className="ec-chip ec-chip--none">&ndash;</span>
              <span className="ec-chip ec-chip--synth ec-chip--b">B</span>
            </div>

            <div className="ec-tile">
              <svg viewBox="0 0 24 20"><path d="M3 9.5 12 2l9 7.5V18a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /></svg>
              <span className="ec-chip ec-chip--c">C</span>
            </div>
          </div>

          <div className="hero-epc__foot">
            <span className="ec-captions">
              <span>Missing records &mdash; synthesised</span>
              <span>High-risk homes &mdash; flagged</span>
              <span>Retrofit priorities &mdash; targeted</span>
            </span>
          </div>
        </div>
      }
    >

      <section className="section">
        <SectionHeading {...pageContent.epc.sections.features} />
        <div className="pitch-grid">
          {cards.features.map((card) => (
            <ContentCard
              key={card.title}
              className="pitch-card"
              icon={card.icon}
              title={card.title}
              description={card.description}
            />
          ))}
        </div>
      </section>

      <section className="section section--alt">
        <SectionHeading {...pageContent.epc.sections.useCases} />
        <div className="use-case-grid">
          {cards.useCases.map((card) => (
            <ContentCard
              key={card.title}
              className="use-case-card"
              icon={card.icon}
              title={card.title}
              description={card.description}
              items={card.items}
            />
          ))}
        </div>
      </section>

      <section className="section">
        <SectionHeading {...pageContent.epc.sections.product} />
        <div className="feature-grid">
          {cards.product.map((card) => (
            <ContentCard
              key={card.title}
              className="feature-card"
              icon={card.icon}
              title={card.title}
              description={card.description}
            />
          ))}
        </div>
      </section>

      <section className="section section--alt" id="pricing">
        <SectionHeading {...pageContent.epc.sections.pricing} />
        <div className="pricing-grid">
          {cards.pricing.map((card) => (
            <ContentCard
              key={card.title}
              className={`pricing-card ${card.featuredBadge ? 'featured' : ''}`.trim()}
              title={card.title}
              price={card.price}
              featuredBadge={card.featuredBadge}
              description={card.description}
              items={card.items}
              ctaLabel={card.ctaLabel}
              ctaHref={card.ctaHref}
              ctaClassName={card.ctaClassName}
            />
          ))}
        </div>
        <div className="pricing-note" role="note" aria-label="EPC pricing explanation">
          <h4>{pageContent.epc.sections.pricingNote.title}</h4>
          <p>{pageContent.epc.sections.pricingNote.body}</p>
          <p className="pricing-note__signal">{pageContent.epc.sections.pricingNote.signal}</p>
          <p className="pricing-note__qualifier">{pageContent.epc.sections.pricingNote.qualifier}</p>
        </div>
      </section>

    </PageLayout>
  );
}

export default EPC;
